import "./DaResetLinkExpired.css";
import { useState } from "react";
import { Button, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DaPageWrapper from "../DaPageWrapper/DaPageWrapper";
import { Translate } from "../../helpers/TranslatedComponent";
import { T_KEYS } from "../../locales/translationKeys";
import useT, { translateStatic } from "../../hooks/useT/useT";
import { CredentialMgmtDataService } from "../../services/CredentialMgmtDataService";
import { useConfig } from "../../contexts/ConfigContext";

const isValidEmail = (emailValue) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailValue);
};

const DaResetLinkExpired = () => {
  const { search, state } = useLocation();
  const [email, setEmail] = useState(state?.email || "");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const credentialService = CredentialMgmtDataService();
  const navigate = useNavigate();
  const { tenant } = useParams();
  const incomingQueryParams = new URLSearchParams(search);
  const brandId = incomingQueryParams.get("brand_id");
  const lang = incomingQueryParams.get("lang");

  const { config } = useConfig();
  const { t } = useT();

  const handleChange = (e) => {
    setEmail(e.target.value);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!email.trim() || !isValidEmail(email)) {
      setAttemptedSubmit(true);
      return;
    }

    setAttemptedSubmit(false);
    setError(null);
    setIsSubmitting(true);

    try {
      const requestQueryParams = new URLSearchParams();
      if (lang) requestQueryParams.set("lang", lang);
      if (brandId) requestQueryParams.set("brand_id", brandId);

      const resource = requestQueryParams.toString()
        ? `/accounts/operations/forgot-password?${requestQueryParams.toString()}`
        : "/accounts/operations/forgot-password";

      const payload = { email, brand_id: brandId };
      await credentialService.forgotPassword(resource, payload);

      const checkEmailPath = requestQueryParams.toString()
        ? `/ciam/v1/${tenant}/accounts/da-check-email?${requestQueryParams.toString()}`
        : `/ciam/v1/${tenant}/accounts/da-check-email`;

      navigate(checkEmailPath, { state: { email } });
    } catch (submitError) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = ["da-forgot-input", attemptedSubmit && !isValidEmail(email) ? "error" : ""]
    .filter(Boolean)
    .join(" ");
  const privacyLink =
    config?.environment === "development" ? t(T_KEYS.DA.SHELL.PRIVACYLINK) : t(T_KEYS.DA.SHELL.PRIVACYProdLINK);
  return (
    <DaPageWrapper
      wrapperClass="da-msg-card da-forgot-card da-page-wrapper"
      iconType="mail"
      title={<Translate tKey={T_KEYS.DA.RESET_LINK.TITLE} />}
      subtitle={<Translate tKey={T_KEYS.DA.RESET_LINK.SUBTITLE} />}
    >
      <form
        className="da-forgot-form"
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        aria-label={translateStatic(T_KEYS.DA.RESET_LINK.TITLE)}
      >
        <label htmlFor="da-forgot-email" className="da-forgot-label">
          <Translate tKey={T_KEYS.DA.FORGOT.LABEL.EMAIL} />
        </label>

        <input
          id="da-forgot-email"
          aria-describedby={attemptedSubmit && !isValidEmail(email) ? "email-error" : undefined}
          aria-invalid={attemptedSubmit && !isValidEmail(email)}
          aria-required="true"
          className={inputClass}
          autoComplete="email"
          type="email"
          placeholder={translateStatic(T_KEYS.DA.FORGOT.PLACEHOLDER.EMAIL)}
          value={email}
          onChange={handleChange}
          autoFocus
        />

        {attemptedSubmit && !isValidEmail(email) && (
          <p id="email-error" role="alert" className="da-password-validation">
            <img src="/ciam/v1/static/images/da/error.svg" alt="" className="da-password-exclamation" />
            <span className="da-password-error-text">{translateStatic(T_KEYS.DA.FORGOT.VALIDATION.EMAIL)}</span>
          </p>
        )}

        {error && (
          <Typography
            variant="caption"
            color="error"
            className="da-msg-error"
            sx={{ mt: 2, display: "block" }}
            aria-live="assertive"
          >
            {error}
          </Typography>
        )}

        <Button
          className="da-button da-forgot-btn"
          type="submit"
          variant="contained"
          disableElevation
          disableRipple
          disableFocusRipple
          aria-busy={isSubmitting}
          aria-disabled={isSubmitting}
          onClick={(e) => {
            if (isSubmitting) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {translateStatic(T_KEYS.DA.FORGOT.BUTTON.CONTINUE)}
        </Button>
      </form>

      <div className="da-forgot-privacy">
        {translateStatic(T_KEYS.DA.FORGOT.PRIVACY.PREFIX)}{" "}
        <a
          className="da-forgot-link"
          href={privacyLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${translateStatic(T_KEYS.DA.FORGOT.PRIVACY.LINK)} (opens in new tab)`}
        >
          <Translate tKey={T_KEYS.DA.FORGOT.PRIVACY.LINK} />
        </a>
      </div>
    </DaPageWrapper>
  );
};

export { DaResetLinkExpired };
